import React, {useContext, useEffect, useRef, useState} from 'react';
import {ScrollView, StyleSheet, TouchableOpacity, View} from 'react-native';
import {theme} from '@common/theme/theme';
import TitleM from '@common/components/text/TitleM';
import TextM from '@common/components/text/TextM';
import {connect} from 'react-redux';
import {apiRequest} from '@data/redux/actions/api';
import {liveService} from '@data/services/liveService';
import {ActivityIndicator, Avatar, Divider, IconButton, Modal, Surface} from 'react-native-paper';
import {useApiStatus} from "@common/hooks/useApiStatus";
import {FontAwesome5} from "@expo/vector-icons";
import {DimensionsContext} from "@common/utils/mobileUtils";
import StudyProgramChapterSelector from "../../common/chapters/StudyProgramChapterSelector";
import SchoolSubjectSelector from "../../common/components/select/SchoolSubjectSelect";
import {useNavigation} from "@react-navigation/native";
import ChampionBubble from "../../student/champions/ChampionBubble";
import {avatarColors} from "@data/constants/studentsConstants";
import StudyItemComponent from "@common/studyPrograms/components/StudyItemComponent";
import Svg, {Circle} from 'react-native-svg';
import {IconImageM} from "@common/components/icons/IconsM";
import moment from "moment";
import {capitalize, parseSubjectIds} from "@data/utility/Utils";
import live from "../../../assets/icons/generic/live.png";
import * as amplitude from "@amplitude/analytics-react-native";


const PrepareLiveScreen = (props) => {
    const dimensions = useContext(DimensionsContext);
    const navigation = useNavigation();
    const [liveOccurrences, setLiveOccurrences] = useState([]);
    const [showChapterSelectionModal, setShowChapterSelectionModal] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [selectedStudyProgramId, setSelectedStudyProgramId] = useState(null);
    const [selectedOccurrence, setSelectedOccurrence] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [updatingKey, setUpdatingKey] = useState(null);
    const hasTrackedTodayPrepared = useRef(false);
    const hasTrackedAllPrepared = useRef(false);
    const trackedSessions = useRef(new Set());

    // Check preparation completion and track events (only once per completion type)
    const checkPreparationCompletion = (occurrences) => {
        if (!occurrences || occurrences.length === 0) return;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Separate occurrences by date
        const todayOccurrences = occurrences.filter(occ => {
            const occDate = new Date(occ.date);
            occDate.setHours(0, 0, 0, 0);
            return occDate.getTime() === today.getTime();
        });

        // Check each occurrence for completion (track single session preparation)
        occurrences.forEach(occurrence => {
            const studyProgramStudents = occurrence.students?.filter(s => s.has_study_program && !s.has_checkpoint) || [];
            const nonStudyProgramStudents = occurrence.students?.filter(s => !s.has_study_program && !s.has_checkpoint) || [];

            const studentsWithChapters = studyProgramStudents.filter(s => s.chapter_id).length;
            const studentsWithSubjects = nonStudyProgramStudents.filter(s => parseSubjectIds(s.subject_ids).length > 0).length;

            const totalStudents = studyProgramStudents.length + nonStudyProgramStudents.length;
            const completedStudents = studentsWithChapters + studentsWithSubjects;

            // Track when a single live session is fully prepared (only once per session)
            if (totalStudents > 0 && completedStudents === totalStudents && !trackedSessions.current.has(occurrence.id)) {
                amplitude.track("live_preparation.live_session_prepared");
                trackedSessions.current.add(occurrence.id);
            }
        });

        // Helper function to check if all students in a set of occurrences are prepared
        const areAllOccurrencesPrepared = (occs) => {
            if (occs.length === 0) return false;

            const studyProgramStudents = occs.reduce((total, occurrence) =>
                total + (occurrence.students?.filter(s => s.has_study_program && !s.has_checkpoint)?.length || 0), 0
            );
            const studentsWithChapters = occs.reduce((total, occurrence) =>
                total + (occurrence.students?.filter(s => (s.chapter_id && s.has_study_program && !s.has_checkpoint))?.length || 0), 0
            );

            const nonStudyProgramStudents = occs.reduce((total, occurrence) =>
                total + (occurrence.students?.filter(s => !s.has_study_program && !s.has_checkpoint)?.length || 0), 0
            );
            const studentsWithSubjects = occs.reduce((total, occurrence) =>
                total + (occurrence.students?.filter(s => (!s.has_study_program && parseSubjectIds(s.subject_ids).length > 0 && !s.has_checkpoint))?.length || 0), 0
            );

            const totalStudents = studyProgramStudents + nonStudyProgramStudents;
            const totalPreparedStudents = studentsWithChapters + studentsWithSubjects;

            return totalStudents > 0 && totalPreparedStudents === totalStudents;
        };

        // Track when all today's sessions are prepared (only once)
        if (!hasTrackedTodayPrepared.current && todayOccurrences.length > 0 && areAllOccurrencesPrepared(todayOccurrences)) {
            amplitude.track("live_preparation.today_lives_prepared");
            hasTrackedTodayPrepared.current = true;
        }

        // Track when all occurrences are prepared (only once)
        if (!hasTrackedAllPrepared.current && occurrences.length > 0) {
            if (areAllOccurrencesPrepared(occurrences)) {
                amplitude.track("live_preparation.today_and_tomorrow_lives_prepared");
                hasTrackedAllPrepared.current = true;
            }
        }
    };

    useEffect(() => {
        props.apiRequest(
            liveService.getTodayTutorLiveOccurrences,
            { id: props.user.id },
            {}
        )
    }, []);

    // Check preparation completion whenever liveOccurrences changes
    useEffect(() => {
        if (!isLoading && liveOccurrences.length > 0) {
            checkPreparationCompletion(liveOccurrences);
        }
    }, [liveOccurrences, isLoading]);

    useApiStatus(liveService.getTodayTutorLiveOccurrences,
        false,
        true,
        (data) => {
            setLiveOccurrences(data);
            setIsLoading(false);
        }
    );

    // Handle API response for chapter update
    useApiStatus(liveService.updateChapterOccurrence,
        false,
        true,
        (data) => {
            props.apiRequest(
                liveService.getTodayTutorLiveOccurrences,
                { id: props.user.id },
                {}
            );
            setShowChapterSelectionModal(false);
            setSelectedStudyProgramId(null);
            setUpdatingKey(null);
        },
        (error) => {
            setShowChapterSelectionModal(false);
            setSelectedStudyProgramId(null);
            setUpdatingKey(null);
        }
    );

    // Handle API response for subject update
    useApiStatus(liveService.updateOccurrenceSubjects,
        false,
        true,
        (data) => {
            props.apiRequest(
                liveService.getTodayTutorLiveOccurrences,
                { id: props.user.id },
                {}
            );
            setUpdatingKey(null);
        },
        (error) => {
            setUpdatingKey(null);
        }
    );

    const handleChapterSelection = (student, occurrence) => {
        console.log("Selected Student Data:", {
            student,
            chapter: student.chapter,
            chapter_id: student.chapter_id
        });
        setSelectedStudent(student);
        setSelectedOccurrence(occurrence);
        setShowChapterSelectionModal(true);
        setSelectedStudyProgramId(null);
    };

    const handleSelectChapter = (chapter) => {
        if (selectedOccurrence && selectedStudent && chapter) {
            // Track student preparation
            amplitude.track("live_preparation.student_prepared");

            const updateKey = `${selectedStudent.id}-${selectedOccurrence.id}`;
            setUpdatingKey(updateKey);
            props.apiRequest(
                liveService.updateChapterOccurrence,
                { id: selectedOccurrence.id },
                {
                    student_id: selectedStudent.id,
                    chapter_id: chapter.id
                }
            );
        }
    };


    const handleSubjectsChange = (student, occurrence, subjects) => {
        console.log('🚀 handleSubjectsChange called with:', {
            studentId: student?.id,
            studentName: student ? `${student.firstname} ${student.lastname}` : 'Unknown',
            occurrenceId: occurrence?.id,
            subjects: subjects,
            subjectsType: typeof subjects,
            isArray: Array.isArray(subjects)
        });

        if (!occurrence || !student) {
            console.error('🚀 Missing required data:', { occurrence: !!occurrence, student: !!student });
            return;
        }

        try {
            const updateKey = `${student.id}-${occurrence.id}`;
            setUpdatingKey(updateKey);

            // Handle different possible structures of subjects parameter
            let subjectIds = [];

            if (Array.isArray(subjects)) {
                // If it's an array, extract the values
                subjectIds = subjects.map(s => {
                    if (s && typeof s === 'object' && s.value !== undefined) {
                        return s.value;
                    }
                    return s; // In case it's already just the ID
                }).filter(id => id !== undefined && id !== null);
            } else if (subjects && typeof subjects === 'object' && subjects.value !== undefined) {
                // If it's a single object with a value property
                subjectIds = [subjects.value];
            } else if (subjects !== undefined && subjects !== null) {
                // If it's a primitive value (like just the ID)
                subjectIds = [subjects];
            }

            console.log('🚀 Processed subjectIds:', subjectIds);

            if (subjectIds.length > 0) {
                // Track student preparation
                amplitude.track("live_preparation.student_prepared");

                props.apiRequest(
                    liveService.updateOccurrenceSubjects,
                    { id: occurrence.id },
                    {
                        student_id: student.id,
                        subject_ids: subjectIds
                    }
                );
            } else {
                console.warn('🚀 No valid subject IDs found, skipping API call');
            }
        } catch (error) {
            console.error('🚀 Error in handleSubjectsChange:', error);
            setUpdatingKey(null);
        }
    };

    const CircularProgress = ({occurrence ,  progress, size = 45 }) => {
        const strokeWidth = 4;
        const radius = (size - strokeWidth) / 2;
        const circumference = radius * 2 * Math.PI;
        const strokeDashoffset = circumference - (progress / 100) * circumference;

        return (
            <View style={{ width: size, height: size, position: 'relative' }}>
                <Svg width={size} height={size}>
                    <Circle
                        stroke={theme.colors.lightMore}
                        fill="none"
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        strokeWidth={strokeWidth}
                    />
                    <Circle
                        stroke={theme.colors.success}
                        fill="none"
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        strokeWidth={strokeWidth}
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        strokeLinecap="round"
                        transform={`rotate(-90 ${size / 2} ${size / 2})`}
                    />
                </Svg>
                <View style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <TextM style={{ fontSize: 12, color: theme.colors.primary, fontWeight: 700 }}>
                        {getSessionStats(occurrence).completedStudents} / {getSessionStats(occurrence).totalStudents}
                    </TextM>
                </View>
            </View>
        );
    };

    const getWelcomeMessage = () => {
        if (isLoading) {
            return "Chargement de tes sessions...";
        }

        // Students with study programs
        const studyProgramStudents = liveOccurrences.reduce((total, occurrence) =>
            total + (occurrence.students?.filter(s => s.has_study_program && !s.has_checkpoint)?.length || 0), 0
        );
        const studentsWithChapters = liveOccurrences.reduce((total, occurrence) =>
            total + (occurrence.students?.filter(s => (s.chapter_id && s.has_study_program && !s.has_checkpoint))?.length || 0), 0
        );

        // Students without study programs
        const nonStudyProgramStudents = liveOccurrences.reduce((total, occurrence) =>
            total + (occurrence.students?.filter(s => !s.has_study_program && !s.has_checkpoint)?.length || 0), 0
        );
        const studentsWithSubjects = liveOccurrences.reduce((total, occurrence) =>
            total + (occurrence.students?.filter(s => (!s.has_study_program && parseSubjectIds(s.subject_ids).length > 0 && !s.has_checkpoint))?.length || 0), 0
        );

        const totalStudents = studyProgramStudents + nonStudyProgramStudents;
        const totalPreparedStudents = studentsWithChapters + studentsWithSubjects;

        if (totalStudents === 0) {
            return "Il n'y a pas de sessions live à préparer pour aujourd'hui.";
        }

        if (totalPreparedStudents === 0) {
            return `Il y a ${totalStudents} élève${totalStudents > 1 ? 's' : ''} à préparer. Sélectionne les chapitres et matières pour faciliter le démarrage de la session ! 📚`;
        }

        if (totalPreparedStudents < totalStudents) {
            const remaining = totalStudents - totalPreparedStudents;
            return `Super ! Il ne te reste plus que ${remaining} élève${remaining > 1 ? 's' : ''} en attente de préparation. Continue comme ça ! ✨`;
        }

        return "Félicitations ! Tu as préparé tous tes élèves. Tu es prêt(e) pour tes sessions ! 🎉";
    };


    const getSelectedSubjectsDisplay = (student, schoolSubjects) => {
        const subjectIds = parseSubjectIds(student.subject_ids);
        if (subjectIds.length === 0) {
            return null;
        }

        // Since we're now using single select, always show the first (and only) subject
        const subject = schoolSubjects?.find(s => s.id === subjectIds[0]);
        return subject?.name || `Subject ${subjectIds[0]}`;
    };

    const formatSchoolSubjectsForSelector = (schoolSubjects, pedagogicalSubjectId = null) => {
        if (!schoolSubjects) return [];

        // On formate la liste de base
        let formattedSubjects = schoolSubjects.map(subject => ({
            label: subject.name,
            value: subject.id,
            icon: subject.icon,
        }));

        if (pedagogicalSubjectId) {
            console.log('🎯 Prioritizing pedagogical subject ID:', pedagogicalSubjectId);
            console.log('🎯 Available subjects before sorting:', formattedSubjects.map(s => ({ id: s.value, name: s.label })));

            // On trie : la matière de l'objectif pédagogique en premier
            formattedSubjects = formattedSubjects.sort((a, b) => {
                if (a.value === pedagogicalSubjectId) return -1;
                if (b.value === pedagogicalSubjectId) return 1;
                return 0;
            });

            formattedSubjects = formattedSubjects.map(subject => {
                if (subject.value === pedagogicalSubjectId) {
                    return {
                        ...subject,
                        label: subject.label,
                        description: "Matière habituelle pour cette session",
                        icon: null,
                        leftComponent: (
                            <View style={{flexDirection: 'row', alignItems: 'center'}}>
                                {!!subject.icon && (
                                    <IconImageM
                                        uri={subject.icon}
                                        width={40}
                                        style={{marginRight: 10}}
                                    />
                                )}
                                <View style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 20,
                                    backgroundColor: theme.colors.accentLighter,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: 10,
                                }}>
                                    <FontAwesome5 name="star" size={20} color={theme.colors.accent} solid/>
                                </View>
                            </View>
                        ),
                    };
                }
                return subject;
            });

            console.log('🎯 Final ordered subjects:', formattedSubjects.map(s => ({ id: s.value, name: s.label })));
        }

        return formattedSubjects;
    };

    const formatSelectedSubjectsForSelector = (student, schoolSubjects) => {
        const subjectIds = parseSubjectIds(student.subject_ids);
        if (subjectIds.length === 0) {
            return [];
        }

        // For single select, only take the first subject (should be only one anyway)
        const subjectId = subjectIds[0];
        const subject = schoolSubjects?.find(s => s.id === subjectId);

        if (subject) {
            return [{
                id: subjectId,
                value: subjectId,
                label: subject.name,
                icon: subject.icon || null,
            }];
        }

        return [];
    };

    const getStudentInitials = (student) => {
        return student.firstname?.charAt(0)?.toUpperCase() || '';
    };

    const getAvatarBackgroundColor = (studentId) => {
        return avatarColors[studentId ? (studentId % avatarColors.length) : 0];
    };

    const getSessionStats = (occurrence) => {
        const studyProgramStudents = occurrence.students?.filter(s => s.has_study_program && !s.has_checkpoint) || [];
        const nonStudyProgramStudents = occurrence.students?.filter(s => !s.has_study_program && !s.has_checkpoint) || [];

        const studentsWithChapters = studyProgramStudents.filter(s => s.chapter_id).length;
        const studentsWithSubjects = nonStudyProgramStudents.filter(s => parseSubjectIds(s.subject_ids).length > 0).length;

        const totalStudents = studyProgramStudents.length + nonStudyProgramStudents.length;
        const completedStudents = studentsWithChapters + studentsWithSubjects;
        const progress = totalStudents > 0 ? (completedStudents / totalStudents) * 100 : 0;

        return { totalStudents, completedStudents, progress };
    };

    const formatSessionTime = (dateString) => {
        const date = new Date(dateString);
        const today = new Date();

        const isToday = date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();

        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const time = `${hours}:${minutes}`;

        if (isToday) {
            return `Aujourd'hui à ${time}`;
        }

        const months = [
            'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
            'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
        ];

        return `${date.getDate()} ${months[date.getMonth()]} à ${time}`;
    };

    return (
        <>
            <ScrollView style={styles.container}>
                <View style={styles.header}>
                    <View style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        paddingRight: 20
                    }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            <IconButton
                                size={32}
                                icon="times"
                                containerColor={theme.colors.transparent}
                                iconColor={theme.colors.primary}
                                onPress={() => {
                                    navigation.goBack()
                                }}
                            />
                            <TitleM
                                style={{
                                    marginLeft: 10,
                                    color: theme.colors.primary,
                                    fontSize: 24,
                                    fontWeight: 'bold'
                                }}
                            >
                                Préparation des sessions
                            </TitleM>
                        </View>
                    </View>
                    <ChampionBubble
                        name="sam"
                        variant="main"
                        texts={[getWelcomeMessage()]}
                        championPosition="left"
                        style={{ marginTop: 20, alignSelf: 'flex-start' }}
                        hideIcons={true}
                    />
                </View>

                <View style={styles.content}>
                    {isLoading ? (
                        <View style={styles.loadingContainer}>
                            <ActivityIndicator
                                size="large"
                                color={theme.colors.primary}
                            />
                            <TextM
                                style={{
                                    marginTop: 15,
                                    color: theme.colors.grey
                                }}
                            >
                                Chargement des sessions...
                            </TextM>
                        </View>
                    ) : (
                        liveOccurrences.map((occurrence, index) => (
                            <Surface key={occurrence.id} style={styles.sessionCard}>
                                <View >
                                    <View style={styles.sessionTime}>
                                        <View style={styles.timeContainer}>
                                            <IconImageM
                                                source={live}
                                                width={22}
                                                style={{
                                                    tintColor: theme.colors.primary
                                                }}
                                            />
                                            <TextM
                                                fontWeight="SemiBold"
                                                color={theme.colors.primary}
                                                fontSize={14}
                                                style={{marginLeft: 5}}
                                                wrap
                                            >
                                                {capitalize(moment(occurrence.date).format("dddd DD MMM [à] HH:mm"))}
                                            </TextM>
                                        </View>
                                        <CircularProgress occurrence={occurrence} progress={getSessionStats(occurrence).progress} />
                                    </View>
                                </View>

                                {occurrence.students?.map((student, studentIndex) => (
                                    <View key={student.id}>
                                        {studentIndex > 0 && <Divider style={styles.divider} />}
                                        <View style={[
                                            styles.studentItem,
                                            student.connection_duration > 0 && styles.disabledSection,
                                            student.connection_duration > 0 && !student.chapter_id && !student.subject_ids?.length && { opacity: 0.7 }
                                        ]}>
                                            <View style={styles.studentInfo}>
                                                <View style={styles.studentDetails}>
                                                    {student.avatar ? (
                                                        <Avatar.Image
                                                            size={45}
                                                            source={{ uri: student.avatar }}
                                                            style={styles.studentAvatar}
                                                        />
                                                    ) : (
                                                        <Avatar.Text
                                                            size={45}
                                                            label={getStudentInitials(student)}
                                                            style={[
                                                                styles.studentAvatar,
                                                                { backgroundColor: getAvatarBackgroundColor(student.id) }
                                                            ]}
                                                        />
                                                    )}
                                                    <View style={styles.studentNameContainer}>
                                                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                                            <TextM style={styles.studentName} fontWeight="SemiBold">
                                                                {student.firstname} {student.lastname}
                                                            </TextM>
                                                            {student.has_study_program && (
                                                                <View style={styles.studyProgramBadge}>
                                                                    <TextM fontSize={10} color={theme.colors.white}>
                                                                        Programme de révision
                                                                    </TextM>
                                                                </View>
                                                            )}
                                                            {student.connection_duration > 0 && (
                                                                <View style={styles.connectedBadge}>
                                                                    <TextM fontSize={10} color={theme.colors.white}>
                                                                        Connecté.e
                                                                    </TextM>
                                                                </View>
                                                            )}
                                                            {student.connection_duration > 0 &&
                                                                ((student.has_study_program && !student.chapter_id) ||
                                                                    (!student.has_study_program && parseSubjectIds(student.subject_ids).length === 0)) && (
                                                                    <View style={styles.warningBadge}>
                                                                        <TextM fontSize={10} color={theme.colors.white}>
                                                                            Non préparé
                                                                        </TextM>
                                                                    </View>
                                                                )}
                                                        </View>
                                                        <TextM style={styles.studentClass}>
                                                            {student.class || 'Classe non définie'}
                                                        </TextM>
                                                    </View>
                                                </View>
                                                {student.has_study_program ? (
                                                    student.chapter_id ? (
                                                        <TouchableOpacity
                                                            onPress={() => handleChapterSelection(student, occurrence)}
                                                            style={{ flex: 1, maxWidth: dimensions.width * 0.5 }}
                                                            disabled={student.connection_duration > 0 || updatingKey === `${student.id}-${occurrence.id}`}
                                                            activeOpacity={student.connection_duration > 0 || updatingKey === `${student.id}-${occurrence.id}` ? 1 : 0.7}
                                                            pointerEvents={student.connection_duration > 0 || updatingKey === `${student.id}-${occurrence.id}` ? 'none' : 'auto'}
                                                        >
                                                            <StudyItemComponent
                                                                item={student}
                                                                containerStyle={{
                                                                    backgroundColor: theme.colors.white,
                                                                    borderWidth: 1,
                                                                    borderColor: theme.colors.primary,
                                                                    borderRadius: 15,
                                                                    opacity: updatingKey === `${student.id}-${occurrence.id}` ? 0.7 : 1,
                                                                }}
                                                                onPress={() => handleChapterSelection(student, occurrence)}
                                                                showDuration={true}
                                                                showSubject={true}
                                                                disabled={student.connection_duration > 0 || updatingKey === `${student.id}-${occurrence.id}`}
                                                            />
                                                        </TouchableOpacity>
                                                    ) : (
                                                        <TouchableOpacity
                                                            style={[
                                                                styles.chapterButton,
                                                                {
                                                                    backgroundColor: theme.colors.primary,
                                                                    borderColor: theme.colors.primary,
                                                                }
                                                            ]}
                                                            disabled={student.connection_duration > 0 || updatingKey === `${student.id}-${occurrence.id}`}
                                                            activeOpacity={student.connection_duration > 0 || updatingKey === `${student.id}-${occurrence.id}` ? 1 : 0.7}
                                                            pointerEvents={student.connection_duration > 0 || updatingKey === `${student.id}-${occurrence.id}` ? 'none' : 'auto'}
                                                            onPress={() => handleChapterSelection(student, occurrence)}
                                                        >
                                                            {updatingKey === `${student.id}-${occurrence.id}` ? (
                                                                <ActivityIndicator size="small" color={theme.colors.white} style={{ marginRight: 8 }} />
                                                            ) : (
                                                                <FontAwesome5
                                                                    name="book"
                                                                    size={12}
                                                                    color={theme.colors.white}
                                                                    style={{ marginRight: 8 }}
                                                                />
                                                            )}
                                                            <TextM
                                                                color={theme.colors.white}
                                                                fontWeight="Bold"
                                                                fontSize={12}
                                                                numberOfLines={1}
                                                                style={{ flex: 1 }}
                                                            >
                                                                {updatingKey === `${student.id}-${occurrence.id}` ? 'Mise à jour...' : 'Choisir un chapitre'}
                                                            </TextM>
                                                        </TouchableOpacity>
                                                    )
                                                ) : (
                                                    <View
                                                        style={{flex: 1, maxWidth: dimensions.width * 0.5}}
                                                        pointerEvents={student.connection_duration > 0 || updatingKey === `${student.id}-${occurrence.id}` ? 'none' : 'auto'}
                                                    >
                                                        <SchoolSubjectSelector
                                                            placeholder="Matière(s) pour la préparation avec Sam"
                                                            schoolSubjects={(() => {
                                                                return formatSchoolSubjectsForSelector(props.schoolSubjects, student.pedagogical_objective?.subject_id);
                                                            })()}
                                                            subjects={formatSelectedSubjectsForSelector(student, props.schoolSubjects)}
                                                            setSubjects={(subjects) => {
                                                                const updatedOccurrences = liveOccurrences.map(occ => {
                                                                    if (occ.id === occurrence.id) {
                                                                        return {
                                                                            ...occ,
                                                                            students: occ.students.map(s => {
                                                                                if (s.id === student.id) {
                                                                                    let subjectIds = [];

                                                                                    if (Array.isArray(subjects)) {
                                                                                        subjectIds = subjects.map(sub => {
                                                                                            if (sub && typeof sub === 'object' && sub.value !== undefined) {
                                                                                                return sub.value;
                                                                                            }
                                                                                            return sub;
                                                                                        }).filter(id => id !== undefined && id !== null);
                                                                                    } else if (subjects && typeof subjects === 'object' && subjects.value !== undefined) {
                                                                                        subjectIds = [subjects.value];
                                                                                    } else if (subjects !== undefined && subjects !== null) {
                                                                                        subjectIds = [subjects];
                                                                                    }

                                                                                    return {
                                                                                        ...s,
                                                                                        subject_ids: subjectIds
                                                                                    };
                                                                                }
                                                                                return s;
                                                                            })
                                                                        };
                                                                    }
                                                                    return occ;
                                                                });
                                                                setLiveOccurrences(updatedOccurrences);
                                                            }}
                                                            onSubjectsChange={(subjects) => handleSubjectsChange(student, occurrence, subjects)}
                                                            maxSelection={1}
                                                            singleSelect={true}
                                                            loading={updatingKey === `${student.id}-${occurrence.id}`}
                                                            disabled={student.connection_duration > 0}
                                                            compactWidth={true}
                                                            chapterStyle={true}
                                                            showConfirmButton={false}
                                                            style={{
                                                                opacity: (student.connection_duration > 0 || updatingKey === `${student.id}-${occurrence.id}`) ? 0.7 : 1,
                                                            }}
                                                        />
                                                    </View>
                                                )}
                                            </View>
                                        </View>
                                    </View>
                                ))}
                            </Surface>
                        ))
                    )}
                </View>
            </ScrollView>

            <Modal
                visible={showChapterSelectionModal}
                onDismiss={() => setShowChapterSelectionModal(false)}
                contentContainerStyle={styles.modalContainer}
            >
                <View style={styles.modalHeader}>
                    <TitleM>Sélectionner un chapitre</TitleM>
                    <TouchableOpacity onPress={() => setShowChapterSelectionModal(false)}>
                        <FontAwesome5 name="times" size={20} color={theme.colors.grey} />
                    </TouchableOpacity>
                </View>
                <View style={styles.modalContent}>
                    <StudyProgramChapterSelector
                        studentIds={selectedStudent?.id}
                        selectedStudyProgramId={selectedStudyProgramId}
                        setSelectedStudyProgramId={setSelectedStudyProgramId}
                        onSelectChapter={handleSelectChapter}
                        dimensions={dimensions}
                        initialSelectedChapter={selectedStudent?.chapter_id ? {
                            id: selectedStudent.chapter_id,
                            name: selectedStudent.chapter_name
                        } : null}
                    />
                </View>
            </Modal>


        </>
    );
};



const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    header: {
        paddingTop: 10,
        paddingBottom: 20,
        position: 'relative',
    },
    content: {
        paddingHorizontal: 20,
        paddingBottom: 20,
    },
    sessionCard: {
        marginBottom: 15,
        borderRadius: 20,
        overflow: 'hidden',
        elevation: 3,
    },
    sessionHeader: {
        padding: 20,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(255,255,255,0.15)',
        backgroundColor: theme.colors.primaryLighter,
        position: 'relative',
    },
    sessionTime: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: theme.colors.primaryLighter,
        paddingHorizontal: 10,
        paddingVertical: 8,
        borderBottomWidth: 1,
        borderColor: theme.colors.lightMore,
    },
    timeContainer: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    sessionTimeText: {
        color: theme.colors.primary,
        fontSize: 14,
        marginLeft: 5,
        fontWeight: '600',
    },
    studentItem: {
        padding: 15,
        backgroundColor: theme.colors.white,
    },
    studentInfo: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    studentDetails: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
    },
    studentAvatar: {
        width: 45,
        height: 45,
        marginRight: 12,
    },
    studentNameContainer: {
        flex: 1,
    },
    studentName: {
        fontSize: 16,
        marginBottom: 2,
        color: theme.colors.primary,
    },
    studentClass: {
        fontSize: 12,
        color: theme.colors.grey,
    },
    divider: {
        height: 1,
        backgroundColor: theme.colors.lightMore,
        opacity: 0.5,
    },
    disabledSection: {
        opacity: 0.5,
    },
    noStudyProgramBadge: {
        backgroundColor: theme.colors.lightMore,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 10,
        marginLeft: 8,
    },
    studyProgramBadge: {
        backgroundColor: theme.colors.primary,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 10,
        marginLeft: 8,
    },
    loadingContainer: {
        padding: 40,
        alignItems: 'center',
        justifyContent: 'center',
    },
    modalContainer: {
        backgroundColor: theme.colors.white,
        padding: 20,
        margin: 20,
        borderRadius: 20,
        height: '80%',
        alignSelf: 'center',
    },
    modalContent: {
        flex: 1,        // Added to ensure content fills the modal
    },
    modalHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.lightMore,
        paddingBottom: 15,
    },
    chapterButton: {
        borderRadius: 15,
        paddingVertical: 8,
        paddingHorizontal: 15,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 140,
        elevation: 1,
        backgroundColor: theme.colors.white,
        borderWidth: 1,
    },
    warningBadge: {
        backgroundColor: theme.colors.warning,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 10,
        marginLeft: 8,
    },
    connectedBadge: {
        backgroundColor: theme.colors.success,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 10,
        marginLeft: 8,
    },
});

const mapStateToProps = (state) => ({
    user: state.data.currentUser?.object,
    schoolSubjects: state.data.staticData?.school_subjects
});

const mapDispatchToProps = {
    apiRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(PrepareLiveScreen);